import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { PremiumCard, PremiumPage, PrimaryButton } from '../components/premium';
import { circleApiErrorMessage, fetchCircleInvitePreview, joinCircle } from '../lib/circles/api';
import type { CircleInvitePreview } from '../types/circles';

export function CircleJoinPage() {
  const { inviteCode = '' } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [preview, setPreview] = useState<CircleInvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    if (!inviteCode) return;
    let cancelled = false;
    setLoading(true);
    void fetchCircleInvitePreview(inviteCode)
      .then((p) => {
        if (!cancelled) setPreview(p);
      })
      .catch((e) => {
        if (!cancelled) setError(circleApiErrorMessage(e, 'Invalid or expired invite link.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [inviteCode]);

  async function handleJoin() {
    if (!inviteCode) return;
    setBusy(true);
    setError(null);
    try {
      await joinCircle(inviteCode);
      setJoined(true);
      if (preview?.id) {
        window.setTimeout(() => nav(`/circles/${preview.id}`, { replace: true }), 800);
      } else {
        window.setTimeout(() => nav('/circles', { replace: true }), 800);
      }
    } catch (e) {
      setError(circleApiErrorMessage(e, 'Could not join this Circle.'));
    } finally {
      setBusy(false);
    }
  }

  if (!user?.circlesEnabled) {
    return (
      <PremiumPage title="Join Circle" onBack={() => nav('/')}>
        <PremiumCard className="!p-5 text-center">
          <p className="text-sm text-zinc-300">Turn on Circles in Profile to accept this invite.</p>
          <Link to="/profile" className="mt-4 inline-block">
            <PrimaryButton>Go to Profile</PrimaryButton>
          </Link>
        </PremiumCard>
      </PremiumPage>
    );
  }

  return (
    <PremiumPage title="Join Circle" onBack={() => nav('/circles')}>
      {loading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-32 rounded-3xl bg-white/5" />
        </div>
      ) : error && !preview ? (
        <PremiumCard className="!p-5 text-center">
          <p className="text-sm text-amber-300">{error}</p>
          <PrimaryButton className="mt-4" onClick={() => nav('/circles')}>
            Back to Circles
          </PrimaryButton>
        </PremiumCard>
      ) : preview ? (
        <PremiumCard variant="hero" className="!p-6 text-center">
          <div className="text-xs font-bold uppercase tracking-widest text-violet-300">Circle invite</div>
          <h2 className="mt-2 font-display text-2xl font-bold text-white">{preview.name}</h2>
          <p className="mt-2 text-sm text-zinc-400">
            {preview.memberCount} {preview.memberCount === 1 ? 'member' : 'members'} · compete on plan execution, not raw lifts
          </p>
          {preview.description ? (
            <p className="mt-3 text-sm text-zinc-500">{preview.description}</p>
          ) : null}
          {error ? <p className="mt-3 text-sm text-amber-300">{error}</p> : null}
          {joined ? (
            <p className="mt-4 text-sm font-semibold text-emerald-300">You joined! Opening Circle…</p>
          ) : (
            <PrimaryButton fullWidth className="mt-6" disabled={busy} onClick={() => void handleJoin()}>
              {busy ? 'Joining…' : 'Join Circle'}
            </PrimaryButton>
          )}
        </PremiumCard>
      ) : null}
    </PremiumPage>
  );
}
